import React, { FC } from 'react';
import { useFormik } from 'formik';
import Yup from '~yup';

import LabelledInput from '~components/atoms/LabelledInput';
import ActionButton from '~components/atoms/ActionButton';
import Text from '~components/atoms/Text';

import UtilFunctions from '~utils/functions';

import * as FormStyles from './_projectManagementCreateProjectForm.module.scss';
import { TCreateNewProjectReduxParams, TCreateNewProjectValidation } from '~types/feat/project';
import { useAppDispatch } from '~redux/hooks';
import combinedActions from '~redux/combinedActions';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { MetalTypeEnum, ProjectAccStatusEnum } from '~utils/enums';

type TProjectManagementCreateProjectForm = {
    submitButtonLabel: string;
    onSubmit: (data: TCreateNewProjectReduxParams) => void;
    error: string;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
}

const ProjectManagementCreateProjectForm: FC<TProjectManagementCreateProjectForm> = ({
    submitButtonLabel,
    error,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
}) => {
    const dispatch = useAppDispatch();
    const { Misc: { blockEnterKey } } = UtilFunctions;

    const projectManagementCreateProjectFormValidation = Yup.object().shape({
        projectName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().validateProjectName().label('Project Name').required('Project Name is required'),
        earmarkAmount: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .required('Earmark quantity is required')
            .numericPositive('Earmark quantity')
            .label('Earmark Quantity')
            .test('no-exponential-notation',
                'Earmark Quantity must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),

        earmarkEndDate: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .required('Maturity period is required')
            .numericPositive('Maturity period')
            .label('Maturity period')
            .test('no-exponential-notation',
                'Maturity Period must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        metalType: Yup.number()
            .required('Metal Type is required')
            .min(1, 'Metal Type is required'),
    });

    const projectManagementCreateProjectFormInitialValues: TCreateNewProjectValidation = {
        projectName: '',
        earmarkAmount: '',
        earmarkEndDate: '',
        description: '',
        metalType: '',
    };

    const formik = useFormik({
        initialValues: projectManagementCreateProjectFormInitialValues,
        validationSchema: projectManagementCreateProjectFormValidation,
        enableReinitialize: true,
        onSubmit: (values) => {
            dispatch(combinedActions.projectCreateNewProjectAttempt({
                ...values,
                earmarkAmount: Number(values.earmarkAmount),
                earmarkEndDate: Number(values.earmarkEndDate),
                status: ProjectAccStatusEnum.Active,
            }));
        },
    });

    const metalTypeOptions = [
        { label: 'Gold', value: MetalTypeEnum.Gold },
        { label: 'Silver', value: MetalTypeEnum.Silver },
    ];

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['form-container']}>
                <LabelledInput
                    id='projectName'
                    name='projectName'
                    label='Project Name* :'
                    value={formik.values.projectName}
                    error={formik.touched.projectName ? formik.errors.projectName : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={FormStyles['span-2']}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='earmarkAmount'
                    name='earmarkAmount'
                    label='Earmark Quantity (RM)* :'
                    value={formik.values.earmarkAmount}
                    error={formik.touched.earmarkAmount ? formik.errors.earmarkAmount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledInput
                    id='earmarkEndDate'
                    name='earmarkEndDate'
                    label='Maturity Period (Years)* :'
                    value={formik.values.earmarkEndDate}
                    error={formik.touched.earmarkEndDate ? formik.errors.earmarkEndDate : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={inputIsDisabled}
                />

                <LabelledDropdown
                    id='metalType'
                    name='metalType'
                    label='Metal Type *:'
                    options={metalTypeOptions}
                    value={
                        metalTypeOptions.find(
                            (option) => option.value === formik.values.metalType
                        )?.label ?? ''
                    }
                    error={
                        formik.touched.metalType && formik.errors.metalType
                            ? formik.errors.metalType
                            : undefined
                    }
                    onSelectOption={(option) => {
                        formik.setFieldValue('metalType', option.value);
                    }}
                    // onBlur={formik.handleBlur}
                    optionSelected={!!formik.values.metalType}
                    noOptionsMessage='No metal type found'
                    disabled={inputIsDisabled}
                />

                <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                    <div className={FormStyles['button-subcontainer']}>
                        <ActionButton
                            label={submitButtonLabel}
                            isLoading={actionButtonIsLoading}
                            type='submit'
                            disabled={actionButtonIsLoading || actionButtonIsDisabled}
                            className={actionButtonClassName}
                        />

                        <Text
                            label={error}
                            isError
                        />
                    </div>
                </div>
            </div>
        </form>
    );
};

export default ProjectManagementCreateProjectForm;
